"use client";

import Link from "next/link";
import { RoleType } from "@/00-shared/const";
import { useState, useRef, useEffect } from "react";
import { Avatar, AvatarImage } from "./ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import Login from "../widgets/Login";
import { LogoutForm } from "../widgets/Login/form/LogoutForm";
import { formatToLocalTime, getLastDay, hashEmailToRandomDigit } from "../shared/helper";
import ToAccount from "../widgets/ToAccount";
import { Label } from "./ui/typography";
import Image from "next/image";
import { Separator } from "./ui/separator";
import ToCollect from "../widgets/ToCollect";
import { cx } from "class-variance-authority";
import { isNil } from "lodash";
import { useUserInfo } from "@/app/[locale]/hooks";
import { SeaBayIcon } from "./ui/icons";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
const rules = ["出海贝是出海匠提供的虚拟商品，可用于出海匠平台内的所有AI工具。", "基础版及以上会员可通过一次性充值出海贝进行工具使用，出海贝在会员有效期内有效。", "会员使用AI工具时，将优先消耗会员权益内工具次数；当日/当月剩余工具次数不足时，将消耗出海贝进行使用。"];
const memberMap: Record<number, {
  url: string;
  titleColor: string;
  color: string;
  title: string;
}> = {
  [RoleType.ENTERPRISE]: {
    url: "/user/enterPriseMemberMini.png",
    titleColor: "text-[#F1EAD6]",
    color: "text-[#ECDED4]",
    title: "企业版会员"
  },
  [RoleType.PROFESSIONAL]: {
    url: "/user/professionalMemberMini.png",
    titleColor: "text-[#D58146]",
    color: "text-[#E98335]",
    title: "专业版会员"
  },
  [RoleType.STANDARD]: {
    url: "/user/standardMemberMini.png",
    titleColor: "text-[#4C3DEE]",
    color: "text-[#A799DB]",
    title: "标准版会员"
  },
  [RoleType.ENTERPRISE_V2]: {
    url: "/user/enterprise_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#EED3B2] bg-clip-text text-transparent",
    color: "text-[#ECDED4]",
    title: "企业版会员"
  },
  [RoleType.EXCLUSIVE]: {
    url: "/user/exclusive_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#CEC0EA] bg-clip-text text-transparent",
    color: "text-[#FAF3EBCC]",
    title: "尊享版会员"
  },
  [RoleType.PROFESSIONAL_V2]: {
    url: "/user/professional_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#FFF9ABCF] bg-clip-text text-transparent",
    color: "text-[#FAF5EBE5]",
    title: "专业版会员"
  },
  [RoleType.BASIC]: {
    url: "/user/basic_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#FFFFFFC7] bg-clip-text text-transparent",
    color: "text-[#FFFFFFD9]",
    title: "基础版会员"
  },
  [RoleType.EXPERIENCE]: {
    url: "/user/experience_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#FFFFFFC7] bg-clip-text text-transparent",
    color: "text-[#FFFFFFD9]",
    title: "体验版会员"
  },
  [RoleType.ENTERPRISE_V3]: {
    url: "/user/enterprise_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#EED3B2] bg-clip-text text-transparent",
    color: "text-[#ECDED4]",
    title: "企业版会员"
  },
  [RoleType.EXCLUSIVE_V3]: {
    url: "/user/exclusive_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#CEC0EA] bg-clip-text text-transparent",
    color: "text-[#FAF3EBCC]",
    title: "尊享版会员"
  },
  [RoleType.PROFESSIONAL_V3]: {
    url: "/user/professional_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#FFF9ABCF] bg-clip-text text-transparent",
    color: "text-[#FAF5EBE5]",
    title: "专业版会员"
  },
  [RoleType.BASIC_V3]: {
    url: "/user/basic_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#FFFFFFC7] bg-clip-text text-transparent",
    color: "text-[#FFFFFFD9]",
    title: "标准版会员"
  },
  [RoleType.EXPERIENCE_V3]: {
    url: "/user/experience_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#FFFFFFC7] bg-clip-text text-transparent",
    color: "text-[#FFFFFFD9]",
    title: "入门版会员"
  }
};
function getMemberByRoleId(roleId: number) {
  if (roleId > 100 && roleId < 1000) {
    return {
      url: "/user/custom_member.webp",
      titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#EED3B2] bg-clip-text text-transparent",
      color: "text-[#ECDED4]",
      title: "企业定制版"
    };
  }
  return memberMap[roleId];
}
export default function UserInfo({
  className
}: {
  className?: string;
}) {
  const {
    user,
    userAuthInfo: authInfo,
    seaBayCount
  } = useUserInfo();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  if (!user || !authInfo || !authInfo.role_list?.[0]) {
    return <Login className={className} />;
  }
  const currentRole = authInfo.role_list[0];
  const currentRoleId = currentRole.id;
  const expriedTime = formatToLocalTime(currentRole.start_at + currentRole.last_time, "yyyy-MM-dd");
  const lastday = getLastDay(currentRole.start_at + currentRole.last_time);
  const avatarImg = user.image ?? `/avatar/${hashEmailToRandomDigit(user.id as string)}.png`;
  const member = getMemberByRoleId(currentRoleId);
  return <Popover open={popoverOpen} onOpenChange={() => setPopoverOpen(true)} data-sentry-element="Popover" data-sentry-component="UserInfo" data-sentry-source-file="UserInfo.tsx">
      <PopoverTrigger data-sentry-element="PopoverTrigger" data-sentry-source-file="UserInfo.tsx">
        <Avatar data-sentry-element="Avatar" data-sentry-source-file="UserInfo.tsx">
          <AvatarImage src={avatarImg} alt="avatar" data-sentry-element="AvatarImage" data-sentry-source-file="UserInfo.tsx" />
        </Avatar>
      </PopoverTrigger>
      <PopoverContent className="mr-2 flex w-fit flex-col gap-4" ref={popoverRef} data-sentry-element="PopoverContent" data-sentry-source-file="UserInfo.tsx">
        <div className="flex items-center gap-2">
          <Avatar data-sentry-element="Avatar" data-sentry-source-file="UserInfo.tsx">
            <AvatarImage src={avatarImg} alt="avatar" data-sentry-element="AvatarImage" data-sentry-source-file="UserInfo.tsx" />
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm font-medium">{user?.username ?? "-"}</span>
            <Label data-sentry-element="Label" data-sentry-source-file="UserInfo.tsx">用户ID: {user?.id}</Label>
          </div>
        </div>
        <div className="flex items-center justify-between border-b-1 border-solid border-gray-3 pb-2 text-sm">
          <Link href="/tiktok/pricing#seabay" className="flex items-center gap-1" data-sentry-element="Link" data-sentry-source-file="UserInfo.tsx">
            <span>出海贝</span>
            <SeaBayIcon width={18} height={17} data-sentry-element="SeaBayIcon" data-sentry-source-file="UserInfo.tsx" />
          </Link>
          {/* <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex cursor-pointer items-center gap-1">
                  <span>出海贝</span>
                  <SeaBayIcon width={18} height={17} />
                </div>
              </TooltipTrigger>
              <TooltipContent className="flex w-[320px] cursor-text select-text flex-col gap-1 bg-gray-1 text-sm">
                {rules.map((rule, index) => (
                  <p key={index}>
                    {index + 1}. {rule}
                  </p>
                ))}
              </TooltipContent>
            </Tooltip>
           </TooltipProvider> */}

          <div className="flex items-center gap-1">
            <span>{seaBayCount}</span>
          </div>
        </div>
        {member && <div>
            {!isNil(lastday) && lastday < 4 && lastday >= 0 && <div className="mb-1 text-xs">
                您的会员将在
                <mark className="inline bg-transparent p-0 text-red-600">
                  {lastday}日
                </mark>
                内到期，请及时续费
              </div>}
            <div className="relative border-b-1 border-solid border-gray-3 pb-2">
              <Image src={member.url} style={{
            height: 70,
            width: 220,
            objectFit: "cover"
          }} className="overflow-hidden rounded-sm" alt="card" width="220" height="70" priority />
              <span className={cx("absolute left-4 top-2 font-bold", member.titleColor)}>
                {member.title}
              </span>
              <span className={cx("absolute left-4 top-10 text-[10px]", member.color)}>
                {expriedTime} 到期
              </span>
            </div>
          </div>}

        <ToAccount data-sentry-element="ToAccount" data-sentry-source-file="UserInfo.tsx" />
        <Separator data-sentry-element="Separator" data-sentry-source-file="UserInfo.tsx" />
        <ToCollect data-sentry-element="ToCollect" data-sentry-source-file="UserInfo.tsx" />
        <Separator data-sentry-element="Separator" data-sentry-source-file="UserInfo.tsx" />
        <LogoutForm data-sentry-element="LogoutForm" data-sentry-source-file="UserInfo.tsx" />
      </PopoverContent>
    </Popover>;
}